import { type PropsWithChildren, type ReactElement, useMemo } from 'react';

import { OverlayProvider } from '../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { TooltipProvider } from '../../core/components/overlay-tooltip/contexts/TooltipContext.js';
import { ChartSeriesActionContext } from '../../core/components/series-action/ChartSeriesActionContext.js';
import type { SeriesActionsTemplate } from '../../core/types/series-actions-template.js';
import { GaugeChartColorContext } from '../contexts/GaugeChartColorContext.js';
import { GaugeChartFormatterContext } from '../contexts/GaugeChartFormatterContext.js';
import { GaugeChartLabelsContext } from '../contexts/GaugeChartLabelsContext.js';
import { GaugeChartLoadingContext } from '../contexts/GaugeChartLoadingContext.js';
import { GaugeChartRangeMaxContext } from '../contexts/GaugeChartRangeMaxContext.js';
import { GaugeChartRangeMinContext } from '../contexts/GaugeChartRangeMinContext.js';
import { GaugeChartSizeContext } from '../contexts/GaugeChartSizeContext.js';
import { GaugeChartThresholdContext } from '../contexts/GaugeChartThresholdContext.js';
import { GaugeChartTooltipNameContext } from '../contexts/GaugeChartTooltipNameContext.js';
import type {
  GaugeChartInternalConfig,
  GaugeChartThresholdIndicatorPropsInternal,
} from '../types/gauge-chart-config-internal.js';
import type { GaugeChartData } from '../types/gauge-chart.js';
import { getValueArcColor } from '../utils/get-value-arc-color.js';

const emptyThresholds: GaugeChartThresholdIndicatorPropsInternal[] = [];

export interface GaugeChartProvidersProps {
  config: GaugeChartInternalConfig;
  width: number;
  height: number;
  value: number | GaugeChartData;
  valueAccessor?: string;
  explicitColor?: string;
  loading: boolean;
  seriesActions?: (series: string | number) => SeriesActionsTemplate;
}

export const GaugeChartProviders = ({
  config,
  width,
  height,
  value,
  valueAccessor,
  explicitColor,
  seriesActions,
  children,
  loading,
}: PropsWithChildren<GaugeChartProvidersProps>) => {
  const {
    formatter,
    max,
    min,
    labelSlot,
    thresholdsIndicators,
    colorRules,
    unit,
  } = config;

  const calculatedColor = getValueArcColor(
    value,
    colorRules,
    explicitColor,
    thresholdsIndicators,
    valueAccessor,
  );

  const seriesActionValue = useMemo(
    () => ({
      chartSeriesActions: seriesActions as (
        series: unknown,
      ) => ReactElement<PropsWithChildren>,
    }),
    [seriesActions],
  );

  const chartSizeValue = useMemo(() => ({ width, height }), [width, height]);

  const formatterContextValue = useMemo(
    () => ({
      formatter,
      unit,
    }),
    [formatter, unit],
  );

  return (
    <OverlayProvider>
      <TooltipProvider persistedStore>
        <ChartSeriesActionContext.Provider value={seriesActionValue}>
          <GaugeChartSizeContext.Provider value={chartSizeValue}>
            <GaugeChartFormatterContext.Provider value={formatterContextValue}>
              <GaugeChartThresholdContext.Provider
                value={thresholdsIndicators ?? emptyThresholds}
              >
                <GaugeChartColorContext.Provider value={calculatedColor}>
                  <GaugeChartLabelsContext.Provider
                    value={{
                      label: labelSlot,
                    }}
                  >
                    <GaugeChartRangeMinContext.Provider value={min}>
                      <GaugeChartRangeMaxContext.Provider value={max}>
                        <GaugeChartLoadingContext.Provider value={loading}>
                          <GaugeChartTooltipNameContext.Provider
                            value={config.tooltip?.name}
                          >
                            {children}
                          </GaugeChartTooltipNameContext.Provider>
                        </GaugeChartLoadingContext.Provider>
                      </GaugeChartRangeMaxContext.Provider>
                    </GaugeChartRangeMinContext.Provider>
                  </GaugeChartLabelsContext.Provider>
                </GaugeChartColorContext.Provider>
              </GaugeChartThresholdContext.Provider>
            </GaugeChartFormatterContext.Provider>
          </GaugeChartSizeContext.Provider>
        </ChartSeriesActionContext.Provider>
      </TooltipProvider>
    </OverlayProvider>
  );
};

GaugeChartProviders['displayName'] = 'GaugeChartProviders';
